import { useEffect, useState } from "react";
import { Sparkle, Copy, Check, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { teleproApi } from "../../lib/teleproApi";

/**
 * ObjectionCoachChip — Vague 11-D.
 *
 * Lightweight coaching chip that appears under the note textarea WHEN the
 * text contains any of the FR objection markers (« pas », « déjà chez »,
 * « trop cher », « pas le temps », etc.). The télépro can click it to
 * fetch 2 short IA replies.
 *
 * Nothing fires automatically — a click is required, so we don't burn
 * tokens on every keystroke.
 *
 * Props :
 *   note : the current draft the télépro is editing
 *   biz  : {id, name, city}
 */
const OBJECTION_MARKERS = [
  "pas intéress", "déjà chez", "déjà avec", "trop cher", "trop élevé",
  "pas le temps", "pas dispo", "j'ai déjà", "on a déjà", "on est déjà",
  "concurrent", "pas besoin", "pas aujourd'hui", "trop tôt", "trop tard",
  "rappelez plus tard", "revoyons ça", "pas convaincu", "pas sûr",
  // VAGUE 21 — nouveaux mots-clés du tutoriel officiel
  "réfléchir", "y penser", "voir plus tard", "site marche", "site déjà",
  "pages jaunes", "google business", "gmb", "garantie", "combien de clients",
  "ça marche", "carte bancaire", "cb", "payant", "budget", "cher"
];

function _looksLikeObjection(note) {
  if (!note || note.length < 15) return false;
  const low = note.toLowerCase();
  return OBJECTION_MARKERS.some((m) => low.includes(m));
}

export default function ObjectionCoachChip({ note, biz, testid = "objection-coach" }) {
  const [replies, setReplies] = useState(null);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [copiedIdx, setCopiedIdx] = useState(null);

  useEffect(() => {
    // Reset panel when user erases the note or types something without
    // an objection anymore — avoids showing stale replies.
    if (!_looksLikeObjection(note)) {
      setReplies(null);
      setVisible(false);
    }
  }, [note]);

  const detected = _looksLikeObjection(note);
  if (!detected) return null;

  const fetchReplies = async () => {
    setLoading(true);
    try {
      // VAGUE 21 — Essai gratuit statique d'abord (matching regex sur ton
      // tutoriel officiel — 0€, 0 latence, réponse mot pour mot approuvée).
      let arr = [];
      try {
        const rStatic = await teleproApi.post("/me/objection-match", { note });
        const matches = rStatic?.data?.matches || [];
        if (matches.length > 0) {
          arr = matches.map((m) => `${m.label}\n\n${m.response}`);
        }
      } catch (_) { /* ignore static fail, will fallback */ }
      // Fallback IA seulement si aucune correspondance statique trouvée
      if (arr.length === 0) {
        const r = await teleproApi.post("/ai/objection", {
          business_id: biz.id, objection: note,
        });
        arr = r?.data?.replies || [];
      }
      if (arr.length === 0) {
        toast.error("L'IA n'a rien retourné — reformule l'objection ?");
      }
      setReplies(arr);
      setVisible(true);
    } catch (e) {
      toast.error(`Erreur IA : ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const copyReply = async (text, idx) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx(null), 1200);
      toast.success("Réponse copiée");
    } catch (_) { /* noop */ }
  };

  return (
    <div data-testid={testid}>
      {!visible && (
        <button
          type="button"
          onClick={fetchReplies}
          disabled={loading}
          className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-yellow-800 bg-yellow-500/10 border border-yellow-500/40 rounded-sm px-2 py-1 hover:bg-yellow-500/20 transition-colors disabled:opacity-50"
          data-testid={`${testid}-fetch`}
          title="L'IA détecte une objection — clique pour obtenir 2 réponses à essayer"
        >
          <Sparkle size={11} weight="fill" className="text-yellow-500"/>
          {loading ? "Analyse…" : "💡 Aide sur cette objection"}
        </button>
      )}
      {visible && replies && (
        <div
          className="bg-yellow-500/10 border border-yellow-500/40 rounded-sm p-2 space-y-1.5 mt-1.5"
          data-testid={`${testid}-replies`}
        >
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-wider font-bold text-yellow-800 flex items-center gap-1">
              <Sparkle size={11} weight="fill" className="text-yellow-500"/>
              2 réponses à essayer
            </div>
            <button
              type="button"
              onClick={() => setVisible(false)}
              className="text-yellow-800/70 hover:text-yellow-800 p-0.5"
              data-testid={`${testid}-close`}
            >
              <X size={10}/>
            </button>
          </div>
          {replies.length === 0 ? (
            <div className="text-[11px] italic text-muted-foreground">Aucune suggestion — reformule l'objection.</div>
          ) : (
            replies.map((r, i) => (
              <div key={i} className="flex items-start gap-1.5 text-xs bg-card border border-yellow-500/20 rounded-sm px-2 py-1.5 group">
                <span className="text-yellow-700 font-bold shrink-0">{i + 1}.</span>
                <span className="flex-1 leading-snug">{r}</span>
                <button
                  type="button"
                  onClick={() => copyReply(r, i)}
                  className="opacity-40 hover:opacity-100 shrink-0 text-muted-foreground hover:text-yellow-800"
                  data-testid={`${testid}-copy-${i}`}
                  title="Copier"
                >
                  {copiedIdx === i ? <Check size={11} weight="bold" className="text-primary"/> : <Copy size={11}/>}
                </button>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
